import { useCallback } from 'react'
import type { AuthError } from '@supabase/supabase-js'
import { useSession } from './session'

// Supabase's password sign-in errors are flat strings on `error.message`;
// the email-link helper in `authErrors.ts` doesn't fit them, so we map the
// handful of common ones here.
function describeSignInError(error: AuthError): string {
  const msg = error.message.toLowerCase()
  if (msg.includes('invalid login credentials') || msg.includes('invalid_credentials')) {
    return 'Email or password is incorrect.'
  }
  if (msg.includes('email not confirmed')) {
    return 'Please confirm your email address before signing in.'
  }
  if (msg.includes('rate limit') || msg.includes('too many')) {
    return 'Too many attempts. Please wait a moment and try again.'
  }
  if (msg.includes('network') || msg.includes('failed to fetch')) {
    return 'Network problem. Check your connection and try again.'
  }
  return 'Could not sign in. Please try again.'
}

/**
 * The captain portal's view of the shared site session (ON-407: was its own
 * Supabase subscription; now `useSession` from ./session).
 *
 * Supports email/password sign-in, Google OAuth (ON-297) and Apple OAuth
 * (ON-298). There is no bespoke sign-up flow: organizers simply share the
 * portal link, and access is resolved server-side from auth.uid() (via
 * get_my_club_captain_role), so any Stazon account can sign in here.
 */
export function useCaptainSession() {
  const { session, loading, signInWithPassword, signInWithOAuth, signOut } = useSession()

  const signIn = useCallback(
    async (email: string, password: string): Promise<string | null> => {
      const error = await signInWithPassword(email, password)
      return error ? describeSignInError(error) : null
    },
    [signInWithPassword]
  )

  // Redirects to Google's consent screen, then back to the current captain
  // portal URL. The `?code=` / fragment Supabase appends is turned into a
  // session on return (detectSessionInUrl), which fires onAuthStateChange.
  // On success this never resolves (the page navigates away); it only returns
  // an error string when the redirect could not be initiated.
  const signInWithGoogle = useCallback(async (): Promise<string | null> => {
    // origin + pathname only — strip any stale `?code=`/`?error=` query so
    // Supabase doesn't append a second `code` param to the callback URL.
    const error = await signInWithOAuth('google', window.location.origin + window.location.pathname, {
      prompt: 'select_account',
    })
    return error ? describeSignInError(error) : null
  }, [signInWithOAuth])

  // Same redirect flow as Google, via Sign in with Apple. Supabase's Apple
  // provider needs a web Services ID configured (see README); on success the
  // page navigates away and this never resolves.
  const signInWithApple = useCallback(async (): Promise<string | null> => {
    const error = await signInWithOAuth('apple', window.location.origin + window.location.pathname)
    return error ? describeSignInError(error) : null
  }, [signInWithOAuth])

  return { session, loading, signIn, signInWithGoogle, signInWithApple, signOut }
}
